import React, { useState } from 'react';
import { Eye, Edit2, Trash2, ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';

const StudentTable = ({
  students = [],
  studentsLoading,
  selectedStudentIds = [],
  onSelectStudent,
  onViewStudent,
  onEditStudent,
  onDeleteStudent,
  onStatusChange,
  currentPage = 1,
  onPageChange,
  itemsPerPage = 20,
  totalStudents,
  isViewOnly = false,
  tenantId,
  refreshTrigger = 0
}) => {
  const [viewAll, setViewAll] = useState(false);
  const [imageErrors, setImageErrors] = useState({});

  const totalPages = Math.ceil(students.length / itemsPerPage);
  const displayedStudents = viewAll
    ? students
    : students.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      onSelectStudent(displayedStudents.map(s => s.studentId || s._id));
    } else {
      onSelectStudent([]);
    }
  };

  const handleSelectStudent = (studentId) => {
    onSelectStudent(prev =>
      prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]
    );
  };

  const getInitials = (first, last) => `${first?.charAt(0) || ''}${last?.charAt(0) || ''}`.toUpperCase();

  const handleDeleteClick = (studentId) => {
    if (!tenantId) {
      console.error('❌ Tenant ID is missing for delete operation');
      alert('Tenant ID is missing. Please refresh the page and try again.');
      return;
    }
    onDeleteStudent(studentId);
  };

  const getPhotoUrl = (studentId) => {
    const timestamp = new Date().getTime();
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/student-profile/${studentId}?t=${timestamp}`;
  };

  const handleImageError = (studentId) => {
    setImageErrors(prev => ({ ...prev, [studentId]: true }));
  };

  if (studentsLoading) {
    return (
      <div className="w-full p-4 space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-center space-x-4 p-3 bg-gray-50 rounded border animate-pulse">
            <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/3"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-gray-500">
        <MoreHorizontal size={48} className="text-gray-300 mb-2" />
        <h3 className="text-lg font-medium">No students found</h3>
        <p className="text-sm">Try changing filters or adding a student.</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-end mb-2">
        <button
          className="px-3 py-1 border rounded text-sm bg-gray-50 hover:bg-gray-100"
          onClick={() => setViewAll(v => !v)}
        >
          {viewAll ? 'Show Paginated' : 'View All'}
        </button>
      </div>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="pl-6 pr-3 py-3 text-left">
              <input
                type="checkbox"
                checked={displayedStudents.length > 0 && selectedStudentIds.length === displayedStudents.length}
                onChange={handleSelectAll}
                className="h-4 w-4 text-blue-600 rounded cursor-pointer"
              />
            </th>
            <th className="px-3 py-3 text-left text-xs font-bold text-gray-500 uppercase">Profile</th>
            <th className="px-3 py-3 text-left text-xs font-bold text-gray-500 uppercase">IDs</th>
            <th className="px-3 py-3 text-left text-xs font-bold text-gray-500 uppercase">Allocation</th>
            <th className="px-3 py-3 text-left text-xs font-bold text-gray-500 uppercase">Status</th>
            <th className="px-3 py-3 text-right text-xs font-bold text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {displayedStudents.map((student) => {
            const studentId = student.studentId || student._id;
            const isSelected = selectedStudentIds.includes(studentId);
            
            // FIX: Use authStatus for active/inactive
            const isActive = student.authStatus === 'active';
            // FIX: Use allocationStatus for unallocated/assigned
            const isUnallocated = student.allocationStatus === 'unallocated' || !student.classId;
            const hasImageError = imageErrors[studentId];

            return (
              <tr key={studentId} className={`hover:bg-blue-50/50 transition ${isSelected ? 'bg-blue-50' : ''}`}>
                <td className="pl-6 pr-3 py-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleSelectStudent(studentId)}
                    className="h-4 w-4 text-blue-600 rounded cursor-pointer"
                  />
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center">
                    <div className="h-10 w-10 flex-shrink-0">
                      {!hasImageError && student.profilePhoto ? (
                        <img 
                          key={`${studentId}-${refreshTrigger}-${student.profilePhoto?.uploadedAt}`}
                          className="h-10 w-10 rounded-full object-cover" 
                          src={getPhotoUrl(studentId)} 
                          alt={`${student.firstName} ${student.lastName}`}
                          onError={() => handleImageError(studentId)}
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold">
                          {getInitials(student.firstName, student.lastName)}
                        </div>
                      )}
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">{student.firstName} {student.lastName}</div>
                      <div className="text-xs text-gray-500">{student.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="text-xs text-gray-900 font-medium">Adm: {student.admissionNumber}</div>
                  <div className="text-xs text-gray-500">USN: {student.USN || '-'}</div>
                </td>
                <td className="px-3 py-3">
                  {isUnallocated ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                      Unallocated
                    </span>
                  ) : (
                    <div className="flex flex-col">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                        {student.className}
                      </span>
                      {student.rollNumber && <span className="text-[10px] text-gray-500 mt-1">Roll: {student.rollNumber}</span>}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3">
                  <button
                    onClick={() => onStatusChange(studentId, isActive ? 'inactive' : 'active')}
                    disabled={isViewOnly}
                    className={`relative inline-flex h-5 w-9 rounded-full transition-colors focus:outline-none ${isActive ? 'bg-green-500' : 'bg-gray-300'} ${isViewOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform mt-0.5 ml-0.5 ${isActive ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </td>
                <td className="px-3 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <button 
                      onClick={() => onViewStudent(student)} 
                      className="text-gray-400 hover:text-blue-600 transition-colors"
                      title="View Details"
                    >
                      <Eye size={16} />
                    </button>
                    <button 
                      onClick={() => onEditStudent(student)} 
                      disabled={isViewOnly}
                      className={`text-gray-400 hover:text-yellow-600 transition-colors ${isViewOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                      title="Edit Student"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button 
                      onClick={() => handleDeleteClick(studentId)} 
                      disabled={isViewOnly}
                      className={`text-gray-400 hover:text-red-600 transition-colors ${isViewOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                      title="Delete Student"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {!viewAll && totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sticky bottom-0">
          <div className="text-sm text-gray-700">
            Page {currentPage} of {totalPages} ({students.length} students)
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => onPageChange(currentPage - 1)} 
              disabled={currentPage === 1} 
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <button 
              onClick={() => onPageChange(currentPage + 1)} 
              disabled={currentPage === totalPages} 
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentTable;